import { Directive, inject, ViewChild } from '@angular/core';
import {
  ColDef,
  ColGroupDef,
  IServerSideGetRowsRequest,
  LoadSuccessParams,
} from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { OBJECT_EDITOR_SERVICE } from 'src/app/core/services/object-editor.token';
import { DataGridComponent } from './data-grid.component';

/**
 * Contract and shared behaviour of a host that composes `<app-data-grid>`.
 * The host template owns the grid and talks to it through its public API;
 * this base never renders, configures or proxies the grid. It names what
 * every host provides (the grid's required inputs) and carries the members
 * every host used to repeat: the translate service, the grid handle and the
 * audit-trail user opener. ObjectEditorService comes through its token
 * because the service's import graph leads back to a grid host (WAP-1680).
 * The column-key namespace (`i18nViewName` + `columnHeader`) stays in the
 * host: legacy views keep other key shapes on purpose (ag-grid.md).
 */
@Directive()
export abstract class DataGridHostBase {
  abstract readonly viewId: ViewId;
  abstract readonly title: string;
  abstract readonly icon: IconProp;
  abstract readonly columnDefs: Array<ColDef | ColGroupDef>;
  abstract readonly fetchRows: (
    request: IServerSideGetRowsRequest,
  ) => Observable<LoadSuccessParams>;

  protected readonly translateService = inject(TranslateService);
  protected readonly objectEditor = inject(OBJECT_EDITOR_SERVICE);

  @ViewChild(DataGridComponent) protected grid?: DataGridComponent;

  /**
   * User cell click (audit trail, triggered-by, ...): opens the user editor and
   * refreshes the grid once it saved. Accepts any user ref carrying an id.
   */
  protected readonly openUser = (
    user: { id?: string | number | null } | null,
  ): void => {
    if (user?.id == null) return;
    this.grid?.refreshWhenSaved(this.objectEditor.editUser(String(user.id)));
  };
}
